import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Building2, FileText, Tag, FolderOpen } from 'lucide-react';
import { getStateAbbr } from '../utils/helpers';

const SearchResults = ({ results, onClose, searchQuery }) => {
  const navigate = useNavigate();

  if (!results || results.total === 0) {
    return (
      <Card className="absolute top-full left-0 right-0 mt-2 p-4 shadow-lg z-50 max-h-96 overflow-y-auto">
        <p className="text-sm text-muted-foreground text-center">
          {searchQuery.length < 2 ? 'Type at least 2 characters to search' : 'No results found'}
        </p>
      </Card>
    );
  }

  const handleNavigate = (type, item) => {
    onClose();
    
    switch(type) {
      case 'license':
        navigate('/licenses');
        break;
      case 'dba':
        navigate('/dbas');
        break;
      case 'filing':
        navigate('/filings');
        break;
      case 'entity':
        navigate('/settings'); // Entity info now in settings
        break;
      default:
        break;
    }
  };

  return (
    <Card className="absolute top-full left-0 right-0 mt-2 p-2 shadow-lg z-50 max-h-96 overflow-y-auto" data-testid="search-results-dropdown">
      <div className="space-y-1">
        {/* Licenses */}
        {results.results.licenses && results.results.licenses.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Licenses ({results.results.licenses.length})
            </div>
            {results.results.licenses.map((license, idx) => (
              <button
                key={`license-${idx}`}
                onClick={() => handleNavigate('license', license)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
                data-testid={`search-result-license-${license.license_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{license.license_type}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {license.license_number} • {license.state}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {license.status}
                </Badge>
              </button>
            ))}
          </div>
        )}

        {/* DBAs */}
        {results.results.dbas && results.results.dbas.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              DBAs ({results.results.dbas.length})
            </div>
            {results.results.dbas.map((dba, idx) => (
              <button
                key={`dba-${idx}`}
                onClick={() => handleNavigate('dba', dba)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
                data-testid={`search-result-dba-${dba.dba_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Tag className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{dba.dba_name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {dba.state}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Filings */}
        {results.results.filings && results.results.filings.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Filings ({results.results.filings.length})
            </div>
            {results.results.filings.map((filing, idx) => (
              <button
                key={`filing-${idx}`}
                onClick={() => handleNavigate('filing', filing)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors text-left"
                data-testid={`search-result-filing-${filing.filing_id}`}
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FolderOpen className="h-4 w-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{filing.filing_type}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {filing.state}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {filing.status}
                </Badge>
              </button>
            ))}
          </div>
        )}

        {/* View All Results */}
        <div className="px-3 py-2 border-t mt-2">
          <p className="text-xs text-muted-foreground text-center">
            Showing {results.total} result{results.total !== 1 ? 's' : ''} for "{searchQuery}"
          </p>
        </div>
      </div>
    </Card>
  );
};

export default SearchResults;
